import { useEffect, useState } from "react";
import { Download, Smartphone, X, Share } from "lucide-react";

/**
 * PWA install helper.
 *
 * - Chromium (Android / desktop): listens for `beforeinstallprompt` and exposes
 *   a styled "Nainštalovať ako appku" button.
 * - iOS Safari: Apple does not fire the event; we detect iOS and show a short
 *   instruction ("Zdieľať -> Pridať na plochu").
 *
 * Can be rendered anywhere. When not installable, returns `null`.
 *
 * Props:
 *  - variant: "button" | "inline"
 */
export default function InstallPWA({ variant = "button" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setInstalled(standalone);

    const ua = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    setIsIOS(iOS);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return;
    }
    if (isIOS) setShowIOSHint(true);
  };

  if (installed) return null;
  if (!deferredPrompt && !isIOS) return null;

  const baseBtn =
    variant === "button"
      ? "flex items-center gap-2 bg-[#00E5FF] text-black font-bold px-4 py-2 rounded-xl hover:bg-[#33EEFF] transition-all"
      : variant === "drawer"
      ? "flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-[#00E5FF]/15 to-[#FF5E00]/10 border border-[#00E5FF]/30 rounded-xl text-[#00E5FF] font-bold hover:from-[#00E5FF]/25 hover:to-[#FF5E00]/20 transition-all"
      : "flex items-center gap-2 text-[#00E5FF] hover:text-[#33EEFF] text-sm font-medium";

  return (
    <>
      <button
        onClick={handleInstall}
        className={baseBtn}
        data-testid="pwa-install-btn"
        title="Nainštalovať ako appku"
      >
        <Download size={16} />
        <span>{variant === "drawer" ? "Pridať na plochu" : "Nainštalovať appku"}</span>
      </button>

      {showIOSHint && (
        <div
          className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSHint(false)}
          data-testid="pwa-ios-hint"
        >
          <div
            className="bg-[#111] border border-white/10 rounded-2xl max-w-sm w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowIOSHint(false)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10"
              data-testid="pwa-ios-hint-close"
            >
              <X size={18} />
            </button>
            <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/20 flex items-center justify-center mb-4">
              <Smartphone size={24} className="text-[#00E5FF]" />
            </div>
            <h3 className="text-xl font-bold mb-2">Pridať na plochu</h3>
            <p className="text-sm text-zinc-400 mb-4">
              Na iPhone / iPade nie je automatický install dialog. Appku si pridáte takto:
            </p>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#00E5FF] text-black text-xs font-bold flex items-center justify-center flex-shrink-0">
                  1
                </span>
                <span>
                  Klepnite na <Share size={14} className="inline mx-1 text-[#00E5FF]" />
                  <strong>Zdieľať</strong> (dole v Safari).
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#00E5FF] text-black text-xs font-bold flex items-center justify-center flex-shrink-0">
                  2
                </span>
                <span>
                  Vyberte <strong>„Pridať na plochu"</strong> (Add to Home Screen).
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#00E5FF] text-black text-xs font-bold flex items-center justify-center flex-shrink-0">
                  3
                </span>
                <span>Potvrďte názov <strong>„SuperMajstri"</strong> a klepnite <strong>Pridať</strong>.</span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
